import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Spinner } from "react-bootstrap";
import TabsNavbar from "../../Common/TabsNavBar/TabsNavbar";
import { fetchProject, fetchSale } from "../../../REDUX/actions/appProjects";
import ProjectGeneral from "./ProjectGeneral";
import ProjectInfo from "./ProjectInfo";
import ProjectInterlocutors from "./ProjectInterlocutors";
import ProjectActions from "./ProjectActions";
import ProjectLocation from "./ProjectLocation";
import ProjectDiscussion from "./ProjectDiscussion";
import ProjectQuotes from "./ProjectQuotes";
import ProjectPayments from "./ProjectPayments";
import ProjectDelivery from "./ProjectDelivery";

const TABS = [
  "General",
  "Project info",
  "Location",
  "Interlocutors",
  "Pipeline action",
  "Quote",
  "Discussion",
];
// Once a quote is ordered: the store's sale to its customer.
const SALE_TABS = [
  "General",
  "Project info",
  "Location",
  "Interlocutors",
  "Pipeline action",
  "Quote",
  "Payments",
  "Delivery",
  "Discussion",
];

/**
 * Right-modal project sheet (deck slides 6/7/11/12 — clear scope). Once a
 * quote is ordered ("Passer la commande"), it is the client sheet: Général
 * shows the sale, Paiements / Livraison are the store ↔ customer side.
 */
const ProjectModal = ({ projectId, initialTab }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation("common");
  const { currentProject, currentLoading } = useSelector(
    (state) => state.appProjectsReducer,
  );
  const [activeTab, setActiveTab] = useState(
    SALE_TABS.includes(initialTab) ? initialTab : TABS[0],
  );
  const [sale, setSale] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    dispatch(fetchProject(projectId, controller.signal));
    return () => controller.abort();
  }, [dispatch, projectId]);

  const project = currentProject?.id === projectId ? currentProject : null;

  // The sale of an ordered quote: the live one, else the latest.
  const saleId = useMemo(() => {
    const orders = (project?.quotes ?? [])
      .map((quote) => quote.order)
      .filter(Boolean);
    return (
      (orders.find((order) => order.state !== "cancelled") ?? orders[0])?.id ??
      null
    );
  }, [project]);
  useEffect(() => {
    if (!saleId) {
      setSale(null);
      return;
    }
    // Another sale's data never stays on screen, nor a sale that failed to load.
    setSale((prev) => (prev?.id === saleId ? prev : null));
    const controller = new AbortController();
    dispatch(fetchSale(saleId, controller.signal)).then((data) => {
      if (!controller.signal.aborted) setSale(data);
    });
    return () => controller.abort();
  }, [dispatch, saleId, project]);

  const tabs = sale ? SALE_TABS : TABS;
  const tab = tabs.includes(activeTab) ? activeTab : TABS[0];

  return (
    <div className="d-flex flex-column h-100">
      <div className="px-5">
        <h2 className="text-blue bold f22 quickSand mb-4">
          {project?.name ?? t("Project")}
          {project?.number ? ` — N°${project.number}` : ""}
        </h2>
        <TabsNavbar tabs={tabs} activeTab={tab} setActiveTab={setActiveTab} />
      </div>
      {!project || currentLoading ? (
        <div className="d-flex justify-content-center py-5">
          <Spinner />
        </div>
      ) : (
        <>
          {tab === "General" && <ProjectGeneral project={project} sale={sale} />}
          {tab === "Project info" && <ProjectInfo project={project} />}
          {tab === "Interlocutors" && (
            <ProjectInterlocutors project={project} />
          )}
          {tab === "Pipeline action" && <ProjectActions project={project} />}
          {tab === "Location" && <ProjectLocation project={project} />}
          {tab === "Quote" && <ProjectQuotes project={project} sale={sale} />}
          {tab === "Payments" && sale && (
            <ProjectPayments sale={sale} onChange={setSale} />
          )}
          {tab === "Delivery" && sale && (
            <ProjectDelivery
              sale={sale}
              projectId={project.id}
              onChange={setSale}
            />
          )}
          {tab === "Discussion" && <ProjectDiscussion project={project} />}
        </>
      )}
    </div>
  );
};

export default ProjectModal;
